import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useInfiniteQuery } from '@tanstack/react-query';

import { isProblemError } from '../../api/client';
import { useGetMe, useListPeople } from '../../api/generated/identity/identity';
import {
  getListChargesQueryKey,
  getListWebhookEventsQueryKey,
  listCharges,
  listWebhookEvents,
  useCancelCharge,
  useCreateCharge,
  useListPrices,
  useRecordManualPayment,
  useRefundCharge,
  useReprocessWebhookEvent,
} from '../../api/generated/ledger/ledger';
import type { Charge, ChargeState, Person, WebhookEvent } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { ConfirmDialog } from '../../design/primitives/Dialog';
import { Field } from '../../design/primitives/Field';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
// O mesmo select com rotulo associado que a folha de pagamento usa para
// escolher o professor. Ver a nota em `PayrollBatch`: promove-lo a primitivo de
// design e decisao de quem cuida do design system, e nao efeito colateral desta
// tela — mas uma segunda copia aqui seria a segunda copia da regra de
// acessibilidade junto.
import { SelectField } from '../enrollment/SelectField';
import { isReauthRequired, useReauthGate, type ReauthGate } from '../reauth/ReauthGate';
import { useMoneyAttempt } from './idempotency';
import { formatMoney } from './money';
import styles from './billing.module.css';

// O teto do contrato para `limit` nas duas listas e 200. Pedir o maximo por
// chamada e o que faz o avanco automatico custar o menor numero de idas ao
// servidor para chegar ao fim da lista.
const PAGE_SIZE = 200;

const CHARGE_STATES: readonly ChargeState[] = [
  'draft',
  'pending',
  'processing',
  'paid',
  'overdue',
  'cancelled',
  'refunded',
  'under_review',
];

/**
 * Financeiro administrativo: faturas, conciliacao e fila de eventos.
 *
 * A fila de eventos e a razao de esta tela existir. Sem ela, a fila de erro do
 * provedor e uma tabela que ninguem olha — e dinheiro que nao conciliou vira
 * dinheiro que ninguem descobre.
 *
 * O portao de reautenticacao nasce **uma vez por tela**, e nao por fatura: duas
 * faturas recusadas ao mesmo tempo abririam dois dialogos empilhados, e o
 * segundo esconderia o primeiro.
 */
export function AdminFinance() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const gate = useReauthGate();

  const [state, setState] = useState<ChargeState | ''>('');
  const [personId, setPersonId] = useState('');
  const [period, setPeriod] = useState('');

  // AS DUAS LISTAS DESTA TELA SEGUEM O CURSOR, e nenhuma seguia.
  //
  // `listCharges` e `listWebhookEvents` respondem no maximo `limit` por chamada e
  // devolvem `nextCursor`; pedir sem cursor trazia a primeira pagina e mais nada,
  // sem nenhum aviso na tela de que havia continuacao. Nas faturas isso e uma
  // busca que mente — o administrador filtra por aluno, nao encontra a fatura e
  // conclui que ela nao existe. Na fila de eventos e pior, porque a fila e a
  // RAZAO DE ESTA TELA EXISTIR (ver a docstring acima): o evento que falhou alem
  // da primeira pagina fica inalcancavel pela interface, e dinheiro que nao
  // conciliou vira dinheiro que ninguem descobre — que e exatamente o desfecho
  // que a tela existe para impedir.
  //
  // O avanco automatico ate `nextCursor` nulo e o padrao da casa (attendance,
  // messaging, schedule, payroll), e o `isFetchNextPageError` na condicao de
  // parada nao e detalhe: sem ele, a pagina que falha no meio deixa `hasNextPage`
  // verdadeiro e `isFetchingNextPage` de volta em falso, o efeito dispara de novo
  // e a tela entra em tempestade de requisicoes contra o servidor que acabou de
  // recusar.
  const chargesQuery = useInfiniteQuery({
    queryKey: getListChargesQueryKey({
      ...(state ? { state } : {}),
      ...(personId ? { personId } : {}),
      ...(period ? { period } : {}),
      limit: PAGE_SIZE,
    }),
    queryFn: ({ pageParam, signal }) =>
      listCharges(
        {
          ...(state ? { state } : {}),
          ...(personId ? { personId } : {}),
          ...(period ? { period } : {}),
          limit: PAGE_SIZE,
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!me,
  });
  const eventsQuery = useInfiniteQuery({
    queryKey: getListWebhookEventsQueryKey({ limit: PAGE_SIZE }),
    queryFn: ({ pageParam, signal }) =>
      listWebhookEvents(
        pageParam === undefined ? { limit: PAGE_SIZE } : { limit: PAGE_SIZE, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!me,
  });
  const pricesQuery = useListPrices({ query: { enabled: !!me } });

  const seguirCursor = (q: {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isFetchNextPageError: boolean;
    fetchNextPage: () => unknown;
  }): void => {
    if (!q.hasNextPage || q.isFetchingNextPage || q.isFetchNextPageError) return;
    void q.fetchNextPage();
  };
  const {
    hasNextPage: temMaisFaturas,
    isFetchingNextPage: buscandoFaturas,
    isFetchNextPageError: falhouPaginaDeFatura,
    fetchNextPage: proximasFaturas,
  } = chargesQuery;
  const {
    hasNextPage: temMaisEventos,
    isFetchingNextPage: buscandoEventos,
    isFetchNextPageError: falhouPaginaDeEvento,
    fetchNextPage: proximosEventos,
  } = eventsQuery;
  useEffect(() => {
    seguirCursor({
      hasNextPage: temMaisFaturas,
      isFetchingNextPage: buscandoFaturas,
      isFetchNextPageError: falhouPaginaDeFatura,
      fetchNextPage: proximasFaturas,
    });
  }, [temMaisFaturas, buscandoFaturas, falhouPaginaDeFatura, proximasFaturas]);
  useEffect(() => {
    seguirCursor({
      hasNextPage: temMaisEventos,
      isFetchingNextPage: buscandoEventos,
      isFetchNextPageError: falhouPaginaDeEvento,
      fetchNextPage: proximosEventos,
    });
  }, [temMaisEventos, buscandoEventos, falhouPaginaDeEvento, proximosEventos]);

  if (!me) return null;

  const charges = (chargesQuery.data?.pages ?? []).flatMap((page) => page.items);
  const events = (eventsQuery.data?.pages ?? []).flatMap((page) => page.items);

  const prices = pricesQuery.data ?? [];
  // `pricesQuery.isError` NAO PODE virar lista vazia em silencio, e virava.
  //
  // Com a consulta falhando, `prices` era `[]`, `hasProvisionalPrice` virava
  // falso e o aviso de preco provisorio SUMIA DA TELA — junto com o motivo pelo
  // qual a emissao de cobranca esta recusando. O administrador via um botao que
  // nao funciona e nenhuma explicacao. Guarda que desaparece quando a consulta
  // que a alimenta falha e guarda-falso: ela so parece existir enquanto tudo vai
  // bem, que e justamente quando ela nao e necessaria.
  const hasProvisionalPrice = prices.some((price) => price.provisional && price.active);

  return (
    <div className={styles.page}>
      <h1>{t('finance.title')}</h1>

      {gate.dialog}

      {hasProvisionalPrice ? (
        <Alert tone="warning" title={t('finance.placeholderPrices.title')}>
          {t('finance.placeholderPrices.detail')}
        </Alert>
      ) : null}
      {pricesQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      <section aria-label={t('finance.prices')}>
        <h2>{t('finance.prices')}</h2>
        <ul className={styles.list}>
          {prices.map((price) => (
            <li
              key={price.id}
              className={`${styles.card} ${price.provisional ? styles.provisional : ''}`}
            >
              <p>
                <strong>{price.label}</strong> · {formatMoney(price.amount, me.locale)}
              </p>
              {/*
                A marca e texto, e nao so cor ou borda: quem le por leitor de
                tela precisa saber que o valor e provisorio tanto quanto quem ve.
              */}
              {price.provisional ? <p>{t('finance.provisionalTag')}</p> : null}
            </li>
          ))}
        </ul>
        {/*
          A leitura mora aqui; a edicao mora na tela de catalogo, porque tirar o
          preco do placeholder exige credencial reconfirmada e um formulario
          inteiro — e nao um campo perdido entre faturas.
        */}
        <p>
          <Link to="/finance/pricing">{t('finance.pricingLink')}</Link>
        </p>
      </section>

      <ChargeIssuer onCreated={() => void chargesQuery.refetch()} />

      <section aria-label={t('finance.charges')}>
        <h2>{t('finance.charges')}</h2>
        <div className={styles.filters}>
          <label>
            {t('finance.filter.state')}
            <select value={state} onChange={(event) => setState(event.target.value as ChargeState | '')}>
              <option value="">{t('finance.filter.any')}</option>
              {CHARGE_STATES.map((value) => (
                <option key={value} value={value}>
                  {t(`billing.chargeState.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <Field
            label={t('finance.filter.person')}
            value={personId}
            onChange={(event) => setPersonId(event.target.value)}
          />
          <Field
            label={t('finance.filter.period')}
            placeholder={t('finance.filter.periodPlaceholder')}
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          />
        </div>

        {/*
          `isFetchNextPageError` entra ao lado de `isError`: o primeiro fala so
          da pagina inicial, e a pagina que falha NO MEIO deixaria a lista
          truncada sem uma linha na tela dizendo por que — o mesmo desfecho do
          defeito que a paginacao acima acabou de fechar.
        */}
        {chargesQuery.isError || falhouPaginaDeFatura ? (
          <Alert tone="error">{t('error.loadFailed')}</Alert>
        ) : null}
        <ul className={styles.list}>
          {charges.map((charge) => (
            <AdminChargeCard
              key={charge.id}
              charge={charge}
              locale={me.locale}
              timezone={me.timezone}
              gate={gate}
              onChanged={() => void chargesQuery.refetch()}
            />
          ))}
        </ul>
      </section>

      <section aria-label={t('finance.webhookQueue')}>
        <h2>{t('finance.webhookQueue')}</h2>
        {/*
          A fila vazia por FALHA e a fila vazia por nada-a-conciliar sao a mesma
          imagem, e significam o oposto. Sem este ramo, um 500 aqui dizia ao
          administrador "nao ha nada pendente" — a unica mensagem que garante que
          ele nao vai procurar.
        */}
        {eventsQuery.isError || falhouPaginaDeEvento ? (
          <Alert tone="error">{t('error.loadFailed')}</Alert>
        ) : null}
        <ul className={styles.list}>
          {events.map((event) => (
            <WebhookEventCard
              key={event.id}
              event={event}
              locale={me.locale}
              timezone={me.timezone}
              onChanged={() => void eventsQuery.refetch()}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

/** Data de hoje em `AAAA-MM-DD`, para o campo de vencimento comecar em algum lugar. */
function today(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * O aluno **escolhido da lista**, e nao um identificador digitado.
 *
 * Mesma forma do `ChosenTeacher` da folha, e pela mesma razao: guardado num
 * `string`, o estado "identificador sem nome" continua exprimivel, e nele um
 * caractere trocado emite cobranca no nome de outra pessoa. Guardando a pessoa
 * que `listPeople` devolveu, so restam dois estados — ninguem escolhido, ou um
 * aluno da lista.
 */
type ChosenStudent = Pick<Person, 'id' | 'displayName'>;

/**
 * Emissao de uma cobranca avulsa.
 *
 * **O vencimento e obrigatorio.** O tipo gerado deixou de aceitar a omissao
 * porque prazo de vencimento e decisao comercial e continua aberto na §15.2:
 * um default silencioso aqui escolheria a decisao no lugar de quem cobra.
 *
 * **Toda recusa vira frase.** Enquanto so o 503 era lido, o resto de
 * `createCharge` — 403 do aluno que nao existe, 422 do corpo recusado, 500 —
 * chegava e nao aparecia: o botao parava de girar, o formulario continuava
 * cheio e nada tinha sido emitido. E o 503 e, hoje, o unico que o servidor
 * **nao** produz nesta operacao (ver `ledger/handler.go`), entao o unico ramo
 * que a tela tinha era o unico que nao podia acontecer.
 */
function ChargeIssuer({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useTranslation();
  // O formulario continua montado depois de emitir, entao a chave **precisa**
  // ser aposentada no sucesso: presa a montagem, a segunda cobranca do dia
  // sairia com a chave da primeira e o servidor devolveria a primeira de volta,
  // calado. A tela diria "emitida" sem nada ter sido emitido.
  const attempt = useMoneyAttempt();
  const create = useCreateCharge({ request: attempt.request });
  // `listPeople` nao e paginada no contrato — devolve o array inteiro filtrado
  // por papel — e por isso ela alcanca qualquer aluno, inclusive o milesimo.
  // `listStudents`, que seria a leitura mais obvia, e paginada por cursor sem
  // busca no servidor: um seletor montado sobre ela so alcancaria a primeira
  // pagina, que e o defeito irmao deste, na folha.
  //
  // Sem filtro de `active`: aluno que encerrou a matricula ainda pode ter
  // cobranca avulsa a emitir, e esconde-lo aqui tornaria a divida dele
  // inalcancavel pela interface.
  const students = useListPeople({ role: 'student' });

  const [student, setStudent] = useState<ChosenStudent | null>(null);
  const [amountCents, setAmountCents] = useState('');
  const [description, setDescription] = useState('');
  const [dueOn, setDueOn] = useState(today);

  const people = students.data ?? [];
  const parsed = Number.parseInt(amountCents, 10);
  const validAmount = Number.isInteger(parsed) && parsed > 0;
  const canSubmit =
    student !== null && description.trim().length > 0 && dueOn.length > 0 && validAmount;

  // O mesmo gate do checkout fecha a emissao enquanto houver preco provisorio.
  // 503 nao e "erro inesperado": e um estado do sistema com saida conhecida, e
  // um alerta generico faria a pessoa tentar de novo indefinidamente.
  const blockedByPlaceholder = isProblemError(create.error) && create.error.status === 503;
  // Todo o resto e uma recusa que a pessoa precisa ler para saber que a
  // cobranca NAO saiu. A frase e a generica de proposito: distinguir 403 de 422
  // aqui exigiria nomear no catalogo motivos que o servidor nao devolve
  // separados — ele responde 403 tanto para "sem permissao" quanto para "aluno
  // inexistente" —, e um texto que adivinha o motivo errado e pior que um que
  // nao adivinha. Mesmo criterio do `ActionFeedback` da folha.
  const refused = create.error !== null && !blockedByPlaceholder;

  return (
    <section aria-label={t('finance.newCharge.title')}>
      <h2>{t('finance.newCharge.title')}</h2>
      <form
        className={styles.filters}
        onSubmit={(event) => {
          event.preventDefault();
          // `student` entra na condicao alem de `canSubmit` porque e ele que o
          // corpo usa logo abaixo: sem esta guarda o compilador nao sabe que a
          // escolha existe, e a alternativa seria uma asercao que mente.
          if (!canSubmit || student === null) return;
          create.mutate(
            {
              data: {
                studentId: student.id,
                amount: { amountCents: parsed, currency: 'BRL' },
                description: description.trim(),
                dueOn,
              },
            },
            {
              onSuccess: () => {
                attempt.settled();
                setStudent(null);
                setAmountCents('');
                setDescription('');
                onCreated();
              },
            },
          );
        }}
      >
        <SelectField
          label={t('finance.newCharge.student')}
          required
          value={student?.id ?? ''}
          placeholder={t('enrollment.choose')}
          options={people.map((person) => ({ value: person.id, label: person.displayName }))}
          // O valor do `select` e sempre resolvido de volta contra a lista: o
          // que entra no estado e a pessoa, e nao a string que o evento trouxe.
          onChange={(value) => setStudent(people.find((person) => person.id === value) ?? null)}
        />
        {/*
          Centavos inteiros, e nao reais com virgula: o contrato guarda
          `amountCents` e a unica conversao para fracao acontece na formatacao.
        */}
        <Field
          label={t('finance.newCharge.amountCents')}
          type="number"
          required
          value={amountCents}
          onChange={(event) => setAmountCents(event.target.value)}
          hint={
            validAmount
              ? formatMoney({ amountCents: parsed, currency: 'BRL' }, 'pt-BR')
              : t('finance.newCharge.amountHint')
          }
        />
        <Field
          label={t('finance.newCharge.description')}
          required
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Field
          label={t('finance.newCharge.dueOn')}
          type="date"
          required
          hint={t('finance.newCharge.dueOnHint')}
          value={dueOn}
          onChange={(event) => setDueOn(event.target.value)}
        />
        <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
          {t('finance.newCharge.submit')}
        </Button>
      </form>

      {/*
        Sem a lista nao ha aluno para escolher, e o seletor vazio se leria como
        "nenhum aluno cadastrado". O erro precisa aparecer, senao a emissao vira
        um beco sem saida silencioso.
      */}
      {students.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      {blockedByPlaceholder ? (
        <Alert tone="warning" title={t('finance.newCharge.blockedTitle')}>
          {t('finance.newCharge.blockedDetail')}
        </Alert>
      ) : null}
      {refused ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
    </section>
  );
}

/**
 * Uma fatura, com as acoes que movem dinheiro.
 *
 * Estorno e cancelamento exigem justificativa **no contrato**, nao por
 * gentileza de interface: o campo entra no corpo e vai para a auditoria. A
 * tela mantem o botao desabilitado enquanto o motivo estiver vazio, para que a
 * recusa aconteca antes da requisicao e nao como um 400 sem explicacao.
 *
 * Quando o servidor exige credencial recente, quem responde e o portao: o
 * dialogo confirma a senha e **a mesma acao roda de novo**, com o motivo que ja
 * estava digitado. Antes disso a tela mandava sair e entrar de novo, e o
 * formulario preenchido ia junto — o defeito que faz alguem desistir de estornar.
 *
 * Estorno e cancelamento **nao acontecem no clique**. Um manda dinheiro de volta
 * pelo provedor e o outro tira a fatura da cobranca; nenhum dos dois volta atras,
 * e um clique errado numa lista de faturas parecidas e barato demais. O dialogo
 * nomeia a fatura, o valor e o aluno, porque "Tem certeza?" nao ajuda quem
 * clicou na linha de cima por engano.
 */
function AdminChargeCard({
  charge,
  locale,
  timezone,
  gate,
  onChanged,
}: {
  readonly charge: Charge;
  readonly locale: string;
  readonly timezone: string;
  readonly gate: ReauthGate;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  /**
   * Qual das duas acoes irreversiveis espera confirmacao — nunca as duas.
   *
   * Um estado so, e nao um booleano por acao: com dois booleanos e possivel
   * escrever o estado em que os dois dialogos estao abertos, e o segundo
   * esconderia o primeiro sobre a mesma fatura.
   */
  const [confirming, setConfirming] = useState<'refund' | 'cancel' | null>(null);
  // `cancelCharge` nao declara `Idempotency-Key` no contrato; `refundCharge`
  // declara, e sem ele o servidor gerado recusa com 400 antes de ler o corpo.
  const attempt = useMoneyAttempt();
  const refund = useRefundCharge({ request: attempt.request });
  const cancel = useCancelCharge();

  const error = refund.error ?? cancel.error;
  // O 403 de reautenticacao ja tem resposta na tela: o dialogo. Repetir a falha
  // num alerta ao lado seria dizer duas vezes a mesma coisa.
  const unexpected = error !== null && !isReauthRequired(error);
  const hasReason = reason.trim().length > 0;

  const execute = (action: 'refund' | 'cancel'): void => {
    // O dialogo fecha **antes** de a acao rodar. Mante-lo aberto com o botao
    // girando seria o natural, mas o portao de reautenticacao pode abrir o dele
    // por cima — e dois dialogos empilhados sobre a mesma fatura escondem o de
    // baixo. O "carregando" continua visivel no botao da propria fatura.
    setConfirming(null);
    gate.run((onReauthRequired) => {
      if (action === 'refund') {
        refund.mutate(
          { chargeId: charge.id, data: { reason } },
          {
            onSuccess: () => {
              // Estorno feito: a chave morre com ele. Um segundo estorno, se
              // houver, e outra operacao e nao pode herdar esta chave.
              attempt.settled();
              onChanged();
            },
            onError: onReauthRequired,
          },
        );
        return;
      }
      cancel.mutate(
        { chargeId: charge.id, data: { reason } },
        { onSuccess: onChanged, onError: onReauthRequired },
      );
    });
  };

  return (
    // Ver a nota em `StudentBilling`: nomear a fatura e o que permite falar de
    // uma delas, em vez de contar textos soltos que tambem existem no filtro.
    <li className={styles.card} aria-label={charge.description}>
      <p>
        <strong>{formatMoney(charge.amount, locale)}</strong> ·{' '}
        <span>{t(`billing.chargeState.${charge.state}`)}</span> · {charge.student.displayName}
      </p>
      <p className={styles.meta}>
        {t('billing.dueOn', {
          date: formatDateInPersonTimezone(charge.dueOn, timezone, locale),
        })}
      </p>

      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <Field
        label={t('finance.reason')}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      <div className={styles.actions}>
        <Button
          variant="secondary"
          disabled={!hasReason}
          loading={refund.isPending}
          onClick={() => setConfirming('refund')}
        >
          {t('finance.refund')}
        </Button>
        <Button
          variant="danger"
          disabled={!hasReason}
          loading={cancel.isPending}
          onClick={() => setConfirming('cancel')}
        >
          {t('finance.cancel')}
        </Button>
      </div>

      {confirming !== null ? (
        <ConfirmDialog
          open
          title={t(CONFIRM_COPY[confirming].title)}
          description={t(CONFIRM_COPY[confirming].body, {
            amount: formatMoney(charge.amount, locale),
            student: charge.student.displayName,
            description: charge.description,
          })}
          confirmLabel={t(CONFIRM_COPY[confirming].action)}
          onCancel={() => setConfirming(null)}
          onConfirm={() => execute(confirming)}
        />
      ) : null}

      <ManualPayment charge={charge} gate={gate} onRecorded={onChanged} />
    </li>
  );
}

/**
 * O texto de cada confirmacao, junto do que ele promete.
 *
 * As frases entraram no catalogo nos tres idiomas, e o texto de reserva que
 * viajava aqui saiu com elas — mante-lo seria uma segunda copia da mesma frase,
 * a que ninguem lembra de traduzir. Cada uma diz a **consequencia**, e nao a
 * acao: quem clica ja sabe que clicou em "Estornar"; o que ele precisa ler e o
 * que acontece com o dinheiro depois.
 */
const CONFIRM_COPY = {
  refund: {
    title: 'finance.refundConfirmTitle',
    body: 'finance.refundConfirmBody',
    action: 'finance.refund',
  },
  cancel: {
    title: 'finance.cancelConfirmTitle',
    body: 'finance.cancelConfirmBody',
    action: 'finance.cancel',
  },
} as const;

/** Instante local em `AAAA-MM-DDTHH:MM`, o formato do `datetime-local`. */
function localNow(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * Registro de pagamento recebido fora do provedor.
 *
 * A Task 11 Step 1 pede os tres juntos — estorno, cancelamento e registro
 * manual — e este ficou de fora ate agora. Sem ele, transferencia bancaria e
 * dinheiro que entrou e que o ledger nunca soube.
 *
 * O valor recebido e um campo proprio, e nao o valor da fatura: pagamento
 * parcial e a razao de a operacao existir, e assumir o total apagaria a
 * diferenca que a conciliacao precisa enxergar.
 */
function ManualPayment({
  charge,
  gate,
  onRecorded,
}: {
  readonly charge: Charge;
  readonly gate: ReauthGate;
  readonly onRecorded: () => void;
}) {
  const { t } = useTranslation();
  // Duas baixas do mesmo valor na mesma fatura sao dinheiro contado duas vezes,
  // e e por isso que o contrato exige a chave aqui. Ela sobrevive ao 403 de
  // reautenticacao — a mesma baixa, repetida — e morre no registro concluido.
  const attempt = useMoneyAttempt();
  const record = useRecordManualPayment({ request: attempt.request });

  const [amountCents, setAmountCents] = useState(String(charge.amount.amountCents));
  const [receivedAt, setReceivedAt] = useState(localNow);
  const [reason, setReason] = useState('');

  const parsed = Number.parseInt(amountCents, 10);
  const validAmount = Number.isInteger(parsed) && parsed > 0;
  const canSubmit = validAmount && receivedAt.length > 0 && reason.trim().length > 0;
  const unexpected = record.error !== null && !isReauthRequired(record.error);

  // Fatura ja liquidada nao recebe pagamento de novo: seria dinheiro contado
  // duas vezes. O botao some com a secao, e nao fica desabilitado sem motivo.
  if (charge.state === 'paid' || charge.state === 'refunded' || charge.state === 'cancelled') {
    return null;
  }

  return (
    <div className={styles.manualPayment}>
      <h3 className={styles.subheading}>{t('finance.manualPayment.title')}</h3>
      <p className={styles.meta}>{t('finance.manualPayment.explained')}</p>

      {unexpected ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <div className={styles.filters}>
        <Field
          label={t('finance.manualPayment.amountCents')}
          type="number"
          required
          value={amountCents}
          onChange={(event) => setAmountCents(event.target.value)}
          hint={
            validAmount
              ? formatMoney({ amountCents: parsed, currency: 'BRL' }, 'pt-BR')
              : t('finance.manualPayment.amountHint')
          }
        />
        <Field
          label={t('finance.manualPayment.receivedAt')}
          type="datetime-local"
          required
          value={receivedAt}
          onChange={(event) => setReceivedAt(event.target.value)}
        />
        <Field
          label={t('finance.manualPayment.reason')}
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Button
          variant="primary"
          disabled={!canSubmit}
          loading={record.isPending}
          onClick={() =>
            gate.run((onReauthRequired) =>
              record.mutate(
                {
                  chargeId: charge.id,
                  data: {
                    amount: { amountCents: parsed, currency: 'BRL' },
                    receivedAt: new Date(receivedAt).toISOString(),
                    reason: reason.trim(),
                  },
                },
                {
                  onSuccess: () => {
                    attempt.settled();
                    setReason('');
                    onRecorded();
                  },
                  onError: onReauthRequired,
                },
              ),
            )
          }
        >
          {t('finance.manualPayment.submit')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Um evento da fila do provedor.
 *
 * `ignored` **mostra o motivo**: um evento silenciosamente descartado e
 * indistinguivel de um evento perdido, e a diferenca entre os dois e o que
 * decide se alguem precisa agir.
 */
function WebhookEventCard({
  event,
  locale,
  timezone,
  onChanged,
}: {
  readonly event: WebhookEvent;
  readonly locale: string;
  readonly timezone: string;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const reprocess = useReprocessWebhookEvent();

  return (
    <li className={styles.card}>
      <p>
        <strong>{event.kind}</strong> · {t(`finance.webhookState.${event.state}`)} ·{' '}
        {t('finance.attempts', { count: event.attempts })}
      </p>
      <p className={styles.meta}>
        {formatDateInPersonTimezone(event.receivedAt, timezone, locale)}
      </p>
      {event.state === 'ignored' && event.ignoredReason ? (
        <p>{t(`finance.ignoredReason.${event.ignoredReason}`)}</p>
      ) : null}
      {event.lastError ? <p className={styles.meta}>{event.lastError}</p> : null}

      {event.state === 'failed' ? (
        <>
          <Field
            label={t('finance.reason')}
            value={reason}
            onChange={(changed) => setReason(changed.target.value)}
          />
          <div className={styles.actions}>
            <Button
              variant="secondary"
              disabled={reason.trim().length === 0}
              loading={reprocess.isPending}
              onClick={() =>
                reprocess.mutate(
                  { webhookEventId: event.id, data: { reason } },
                  { onSuccess: onChanged },
                )
              }
            >
              {t('finance.reprocess')}
            </Button>
          </div>
        </>
      ) : null}
    </li>
  );
}
